import { CAPACITY_MOST, CROSSING_KINDS, DEPTH_LOWEST, PLACE_LIMIT, ROOM_SIDE_MOST, SEAM_STATES, depthOf, rectOf, seamKey, seamSide, touching } from "./layout.ts";
import type { MapDocument, MapGroup, Seam, SeamCrossing, WorldDocument, WorldGrid } from "./types.ts";

/**
 * The rules a world's layout keeps (ADR-109 decisions 2, 3, 6 and 7; ADR-122 decisions 1.2, 1.3, 4.2 and 8),
 * which the reader runs on every document and every layout command on the document it leaves, so no valid edit
 * makes a document unreadable. A room's host's links are not among them: they are issues (`hostIssues`).
 */

const COLOUR = /^#[0-9a-fA-F]{6}$/;

/**
 * Checks one place against the world's chunk lattice and the range.
 *
 * @param map The map.
 * @param chunkSize The world's chunk size.
 * @throws Error naming the map and the field when a coordinate is not a whole number, is off the lattice or is
 *   out of range.
 */
function checkPlace(map: MapDocument, chunkSize: number): void {
  const place = map.place;
  if (place === undefined) {
    return;
  }
  if (place.depth !== undefined) {
    if (!Number.isInteger(place.depth)) {
      throw new Error(`map ${map.id}: place.depth must be a whole number`);
    }
    if (place.depth < DEPTH_LOWEST || place.depth > 0) {
      throw new Error(`map ${map.id}: place.depth ${place.depth} is outside ${DEPTH_LOWEST} to 0`);
    }
  }
  for (const axis of ["x", "y"] as const) {
    const value = place[axis];
    if (!Number.isInteger(value)) {
      throw new Error(`map ${map.id}: place.${axis} must be a whole number of tiles`);
    }
    if (value < -PLACE_LIMIT || value > PLACE_LIMIT) {
      throw new Error(`map ${map.id}: place.${axis} ${value} is outside -${PLACE_LIMIT} to ${PLACE_LIMIT}`);
    }
    if (value % chunkSize !== 0) {
      throw new Error(`map ${map.id}: place.${axis} ${value} is not a multiple of the chunk size ${chunkSize}`);
    }
  }
}

/**
 * Checks what a map's sitting and sharing say of it alone (ADR-122 decisions 1.2, 1.3 and 4.2): `place` and
 * `host` exclusive, sharing by a party, a character or an owner only for a room, a capacity from 1 to
 * `CAPACITY_MOST`, and a room's sides whole chunks of at most `ROOM_SIDE_MOST` tiles. Where a host leads is not
 * checked here: a broken host is an issue (`hostIssues`), never a document rule.
 *
 * @param map The map.
 * @param chunkSize The world's chunk size.
 * @throws Error naming the map and the field.
 */
function checkSitting(map: MapDocument, chunkSize: number): void {
  if (map.place !== undefined && map.host !== undefined) {
    throw new Error(`map ${map.id}: place and host are both set; a map is on the grid or a room, not both`);
  }
  if (map.share !== undefined && map.host === undefined) {
    throw new Error(`map ${map.id}: share ${map.share} needs a host; only a room is shared by a party, a character or an owner`);
  }
  if (map.capacity !== undefined) {
    if (!Number.isInteger(map.capacity)) {
      throw new Error(`map ${map.id}: capacity must be a whole number`);
    }
    if (map.capacity < 1 || map.capacity > CAPACITY_MOST) {
      throw new Error(`map ${map.id}: capacity ${map.capacity} is outside 1 to ${CAPACITY_MOST}`);
    }
  }
  if (map.host !== undefined) {
    for (const side of ["width", "height"] as const) {
      if (map[side] > ROOM_SIDE_MOST) {
        throw new Error(`map ${map.id}: a room's ${side} ${map[side]} is over ${ROOM_SIDE_MOST} tiles`);
      }
      if (map[side] % chunkSize !== 0) {
        throw new Error(`map ${map.id}: a room's ${side} ${map[side]} is not a whole number of chunks of ${chunkSize}`);
      }
    }
  }
}

/**
 * Checks that no two placed maps of one depth overlap; maps of different depths may (ADR-122 decision 1.2).
 *
 * @param maps The world's maps.
 * @throws Error naming the later map and the one it overlaps.
 */
function checkOverlaps(maps: readonly MapDocument[]): void {
  const placed = maps.filter((m) => m.place !== undefined);
  for (let i = 1; i < placed.length; i += 1) {
    const map = placed[i] as MapDocument;
    const r = rectOf(map);
    for (let j = 0; j < i; j += 1) {
      const other = placed[j] as MapDocument;
      if (depthOf(other) !== depthOf(map)) {
        continue;
      }
      const o = rectOf(other);
      if (r !== null && o !== null && r.x < o.x + o.w && o.x < r.x + r.w && r.y < o.y + o.h && o.y < r.y + r.h) {
        throw new Error(`map ${map.id}: place overlaps map ${other.id}`);
      }
    }
  }
}

/**
 * Checks the grid against the chunk lattice.
 *
 * @param grid The grid.
 * @param chunkSize The world's chunk size.
 * @throws Error naming the field when the cell is not a positive multiple of the chunk size or an origin is not
 *   a whole number of tiles within range.
 */
function checkGrid(grid: WorldGrid, chunkSize: number): void {
  if (!Number.isInteger(grid.cell) || grid.cell <= 0 || grid.cell % chunkSize !== 0) {
    throw new Error(`grid.cell ${grid.cell} is not a positive multiple of the chunk size ${chunkSize}`);
  }
  for (const axis of ["x", "y"] as const) {
    if (!Number.isInteger(grid[axis])) {
      throw new Error(`grid.${axis} must be a whole number of tiles`);
    }
    if (Math.abs(grid[axis]) > PLACE_LIMIT) {
      throw new Error(`grid.${axis} ${grid[axis]} is outside -${PLACE_LIMIT} to ${PLACE_LIMIT}`);
    }
  }
}

/**
 * Checks the map groups: unique ids, names that are not blank, colours of the form `#rrggbb`.
 *
 * @param groups The groups.
 * @throws Error naming the group's index and field.
 */
function checkGroups(groups: readonly MapGroup[]): void {
  const seen = new Set<string>();
  groups.forEach((group, i) => {
    if (group.id.trim() === "") {
      throw new Error(`mapGroups[${i}].id is blank`);
    }
    if (seen.has(group.id)) {
      throw new Error(`mapGroups[${i}].id ${group.id} is a duplicate`);
    }
    seen.add(group.id);
    if (group.name.trim() === "") {
      throw new Error(`mapGroups[${i}].name is blank`);
    }
    if (!COLOUR.test(group.colour)) {
      throw new Error(`mapGroups[${i}].colour ${group.colour} is not a colour of the form #rrggbb`);
    }
  });
}

/**
 * Checks one crossing of a seam.
 *
 * @param seam The seam's key, for messages.
 * @param crossing The crossing.
 * @param length The length of map `a`'s edge the seam runs along.
 * @throws Error naming the seam and the crossing.
 */
function checkCrossing(seam: string, crossing: SeamCrossing, length: number): void {
  if (crossing.name.trim() === "") {
    throw new Error(`seam ${seam}: a crossing's name is blank`);
  }
  if (!CROSSING_KINDS.includes(crossing.kind)) {
    throw new Error(`seam ${seam}: crossing ${crossing.name} is of no kind ${CROSSING_KINDS.join(", ")}`);
  }
  const { from, to } = crossing;
  if ((from === undefined) !== (to === undefined)) {
    throw new Error(`seam ${seam}: crossing ${crossing.name} gives ${from === undefined ? "to without from" : "from without to"}`);
  }
  if (from === undefined || to === undefined) {
    return;
  }
  if (!Number.isInteger(from) || !Number.isInteger(to) || from < 0 || to > length) {
    throw new Error(`seam ${seam}: crossing ${crossing.name} spans ${from} to ${to}, outside 0 to ${length}`);
  }
  if (from > to) {
    throw new Error(`seam ${seam}: crossing ${crossing.name} spans ${from} to ${to}, which runs backwards`);
  }
}

/**
 * Checks one seam: both maps exist and are placed, `a` is the northern or western of a pair that shares a
 * stretch of edge, its state is one of three, a closed seam holds no crossing, and each crossing's name is
 * unique and its span within `a`'s edge.
 *
 * @param world The world.
 * @param seam The seam.
 * @throws Error naming the seam and what is wrong.
 */
export function checkSeam(world: WorldDocument, seam: Seam): void {
  const key = seamKey(seam);
  const ends = (["a", "b"] as const).map((end) => {
    const map = world.maps.find((m) => m.id === seam[end]);
    if (map === undefined) {
      throw new Error(`seam ${key}: ${end} names map ${seam[end]}, which the world lacks`);
    }
    if (map.place === undefined) {
      throw new Error(`seam ${key}: ${end} names map ${map.id}, which has no place`);
    }
    return map;
  });
  const [a, b] = ends as [MapDocument, MapDocument];
  if (!SEAM_STATES.includes(seam.state)) {
    throw new Error(`seam ${key}: state ${seam.state} is not one of ${SEAM_STATES.join(", ")}`);
  }
  if (depthOf(a) !== depthOf(b)) {
    throw new Error(`seam ${key}: map ${a.id} stands at depth ${depthOf(a)} and map ${b.id} at depth ${depthOf(b)}; a seam joins maps of one depth`);
  }
  const along = seamSide(a, b);
  if (along === null) {
    throw new Error(touching(a, b) === null ? `seam ${key}: the maps share no edge` : `seam ${key}: a must be the northern or western map`);
  }
  if (seam.state === "closed" && seam.crossings.length > 0) {
    throw new Error(`seam ${key}: a closed seam holds no crossing`);
  }
  const names = new Set<string>();
  for (const crossing of seam.crossings) {
    if (names.has(crossing.name)) {
      throw new Error(`seam ${key}: crossing ${crossing.name} is a duplicate`);
    }
    names.add(crossing.name);
    checkCrossing(key, crossing, along.length);
  }
}

/**
 * Checks a world's layout against every rule of ADR-109 decisions 2, 3, 6 and 7 and ADR-122 decisions 1.2,
 * 1.3, 4.2 and 8: each place on the chunk lattice, within range and at a depth from -8 to 0, no two placed maps
 * of one depth overlapping, each map on the grid or a room and shared accordingly, each map's group one the
 * world holds, the groups and the grid well formed, and every seam between two placed maps of one depth that
 * share a stretch of edge, at most one a pair. The reader runs it on every document and the layout commands on the document they leave, so no valid
 * edit makes a document unreadable.
 *
 * @param world The world.
 * @throws Error naming the map, the group or the seam and the field that breaks a rule.
 */
export function validateDocument(world: WorldDocument): void {
  for (const map of world.maps) {
    checkSitting(map, world.chunkSize);
    checkPlace(map, world.chunkSize);
  }
  checkOverlaps(world.maps);
  if (world.grid !== undefined) {
    checkGrid(world.grid, world.chunkSize);
  }
  const groups = world.mapGroups ?? [];
  checkGroups(groups);
  for (const map of world.maps) {
    if (map.mapGroup !== undefined && !groups.some((group) => group.id === map.mapGroup)) {
      throw new Error(`map ${map.id}: mapGroup ${map.mapGroup} is not a group of the world`);
    }
  }
  const keys = new Set<string>();
  for (const seam of world.seams ?? []) {
    const key = seamKey(seam);
    if (keys.has(key)) {
      throw new Error(`seam ${key} is a duplicate`);
    }
    keys.add(key);
    checkSeam(world, seam);
  }
}
